import React, { useState } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { getLootboxItems, getLootboxRarities } from '../../constants/lootbox';
import { VFX_EFFECTS } from '../../constants/vfx';
import { useToast } from '../ToastContext';
import ItemIcon from './ItemIcon';

function CollectionTracker({ inventory, user, userData, onUserDataChange, isS2 }) {
  const toast = useToast();
  const [claiming, setClaiming] = useState(false);
  const [optimisticUserData, setOptimisticUserData] = useState(null);
  const [activeTab, setActiveTab] = useState('base');
  const effectiveUserData = optimisticUserData ? { ...userData, ...optimisticUserData } : userData;

  // Count owned items by id
  const ownedItems = inventory.reduce((acc, item) => {
    const key = item.id;
    if (!acc[key]) {
      acc[key] = {
        count: 0,
        hasShiny: false,
        hasNormal: false,
        hasStatTrak: false,
        vfxSet: new Set(),
      };
    }
    acc[key].count++;
    if (item.isShiny) {
      acc[key].hasShiny = true;
    } else {
      acc[key].hasNormal = true;
    }
    if (item.isStatTrak) {
      acc[key].hasStatTrak = true;
    }
    if (item.vfx && Array.isArray(item.vfx)) {
      item.vfx.forEach(v => acc[key].vfxSet.add(v));
    }
    return acc;
  }, {});

  const allItems = getLootboxItems(isS2);
  const allRarities = getLootboxRarities(isS2);
  const rarityTiersReversed = Object.keys(allRarities).reverse();

  // Shiny collection: unique items that have a shiny variant
  const shinyCollected = Object.keys(ownedItems).filter(id => ownedItems[id].hasShiny).length;
  const shinyTotal = allItems.length;

  // StatTrak collection: unique items that have a StatTrak variant
  const statTrakCollected = Object.keys(ownedItems).filter(id => ownedItems[id].hasStatTrak).length;
  const statTrakTotal = allItems.length;

  // VFX collection: unique items that have any VFX variant
  const vfxItemsCollected = Object.keys(ownedItems).filter(id => ownedItems[id].vfxSet.size > 0).length;
  const vfxItemsTotal = allItems.length;

  // VFX effects: unique VFX effects collected across all items
  const allOwnedVfx = new Set();
  Object.values(ownedItems).forEach(o => o.vfxSet.forEach(v => allOwnedVfx.add(v)));
  const vfxEffectsCollected = allOwnedVfx.size;
  const vfxEffectsTotal = VFX_EFFECTS.length;

  // Combined VFX progress (items + effects)
  const vfxCombinedCollected = vfxItemsCollected + vfxEffectsCollected;
  const vfxCombinedTotal = vfxItemsTotal + vfxEffectsTotal;

  // Group all items by rarity (display rarest first)
  const itemsByRarity = rarityTiersReversed.map(rarity => ({
    rarity,
    items: allItems.filter(item => item.rarity === rarity)
  }));

  // Calculate overall completion
  const totalItems = allItems.length;
  const collectedItems = Object.keys(ownedItems).length;
  const completionPercentage = ((collectedItems / totalItems) * 100).toFixed(1);

  // S1 milestones: cumulative tiers, lootbox rewards
  const S1_COLLECTION_MILESTONES = [
    { id: 'common', name: 'Common Collector', description: 'Collect all Common items', tiers: ['COMMON'], rewards: { normalLootboxes: 50 }, icon: '🎁' },
    { id: 'uncommon', name: 'Uncommon Collector', description: 'Collect all Common and Uncommon items', tiers: ['COMMON', 'UNCOMMON'], rewards: { normalLootboxes: 100 }, icon: '🎁' },
    { id: 'rare', name: 'Rare Collector', description: 'Collect all Common, Uncommon, and Rare items', tiers: ['COMMON', 'UNCOMMON', 'RARE'], rewards: { eliteLootboxes: 3 }, icon: '✨' },
    { id: 'epic', name: 'Epic Collector', description: 'Collect all Common, Uncommon, Rare, and Epic items', tiers: ['COMMON', 'UNCOMMON', 'RARE', 'EPIC'], rewards: { eliteLootboxes: 5 }, icon: '🎆' },
    { id: 'legendary', name: 'Legendary Collector', description: 'Collect all Common through Legendary items', tiers: ['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY'], rewards: { godLootboxes: 3 }, icon: '👑' },
    { id: 'mythic', name: 'Mythic Collector', description: 'Collect all Common through Mythic items', tiers: ['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY', 'MYTHIC'], rewards: { godLootboxes: 5 }, icon: '🌟' },
  ];

  // S2 milestones: per-rarity independent, token rewards
  const S2_MILESTONES = [
    { id: 'common', name: 'Common Collector', description: 'Collect all Common items', tiers: ['COMMON'], rewards: { tokenType: 'crewmate', amount: 10 }, icon: '🎁' },
    { id: 'uncommon', name: 'Uncommon Collector', description: 'Collect all Uncommon items', tiers: ['UNCOMMON'], rewards: { tokenType: 'crewmate', amount: 25 }, icon: '🎁' },
    { id: 'rare', name: 'Rare Collector', description: 'Collect all Rare items', tiers: ['RARE'], rewards: { tokenType: 'impostor', amount: 5 }, icon: '✨' },
    { id: 'epic', name: 'Epic Collector', description: 'Collect all Epic items', tiers: ['EPIC'], rewards: { tokenType: 'impostor', amount: 15 }, icon: '🎆' },
    { id: 'legendary', name: 'Legendary Collector', description: 'Collect all Legendary items', tiers: ['LEGENDARY'], rewards: { tokenType: 'impostor', amount: 35 }, icon: '👑' },
    { id: 'mythic', name: 'Mythic Collector', description: 'Collect all Mythic items', tiers: ['MYTHIC'], rewards: { tokenType: 'impostor', amount: 75 }, icon: '🌟' },
    { id: 'galaxy', name: 'Galaxy Collector', description: 'Collect all Galaxy items', tiers: ['GALAXY'], rewards: { tokenType: 'impostor', amount: 150 }, icon: '🌌' },
  ];

  const COLLECTION_MILESTONES = isS2 ? S2_MILESTONES : S1_COLLECTION_MILESTONES;

  // Check if a milestone is completed
  const checkMilestoneCompletion = (milestone) => {
    for (const tier of milestone.tiers) {
      const tierItems = allItems.filter(item => item.rarity === tier);
      const hasAll = tierItems.every(item => ownedItems[item.id]);
      if (!hasAll) return false;
    }
    return true;
  };

  // Get claimed rewards from effectiveUserData (includes optimistic updates)
  const claimedRewards = effectiveUserData?.collectionRewards || {};

  // Claim a milestone reward
  async function claimMilestoneReward(milestone) {
    if (!user || !userData) return;
    if (claiming) return;
    if (claimedRewards[milestone.id]) return;
    if (!checkMilestoneCompletion(milestone)) return;

    setClaiming(true);
    // Optimistically mark milestone claimed and update rewards
    const r = milestone.rewards;
    if (isS2) {
      // S2: token rewards
      const tokenField = r.tokenType === 'crewmate' ? 'crewmateTokens' : 'imposterTokens';
      setOptimisticUserData({
        collectionRewards: { ...(userData?.collectionRewards || {}), [milestone.id]: true },
        [tokenField]: (userData?.[tokenField] || 0) + r.amount,
      });
    } else {
      // S1: lootbox rewards
      const prevStock = userData?.lootboxStock || {};
      setOptimisticUserData({
        collectionRewards: { ...(userData?.collectionRewards || {}), [milestone.id]: true },
        lootboxStock: {
          ...prevStock,
          normal: (prevStock.normal || 0) + (r.normalLootboxes || 0),
          shiny: (prevStock.shiny || 0) + (r.shinyLootboxes || 0),
          elite: (prevStock.elite || 0) + (r.eliteLootboxes || 0),
          god: (prevStock.god || 0) + (r.godLootboxes || 0),
        },
      });
    }

    try {
      const claimFn = httpsCallable(functions, 'claimMilestoneReward');
      await claimFn({ milestoneId: milestone.id });
      setOptimisticUserData(null);
      await onUserDataChange();
      toast.success(`🎉 Claimed ${milestone.name} reward!\n${formatRewards(milestone.rewards)}`);
    } catch (error) {
      setOptimisticUserData(null);
      await onUserDataChange();
      console.error('Error claiming milestone reward:', error);
      toast.error(error.message || 'Failed to claim reward. Please try again.');
    } finally {
      setClaiming(false);
    }
  }

  // Format rewards for display
  const formatRewards = (rewards) => {
    if (rewards.tokenType) {
      // S2 token rewards
      const tokenName = rewards.tokenType === 'crewmate' ? 'Crewmate' : 'Impostor';
      return `${rewards.amount} ${tokenName} Token${rewards.amount > 1 ? 's' : ''}`;
    }
    // S1 lootbox rewards
    const parts = [];
    if (rewards.normalLootboxes) parts.push(`${rewards.normalLootboxes} Normal Lootboxes`);
    if (rewards.shinyLootboxes) parts.push(`${rewards.shinyLootboxes} Shiny Lootboxes`);
    if (rewards.eliteLootboxes) parts.push(`${rewards.eliteLootboxes} Elite Lootboxes`);
    if (rewards.godLootboxes) parts.push(`${rewards.godLootboxes} GOD BOX${rewards.godLootboxes > 1 ? 'ES' : ''}`);
    return parts.join(', ');
  };

  const COLLECTION_TABS = [
    { key: 'base', label: '📊 Base', color: '#5BC0DE' },
    { key: 'shiny', label: '✨ Shiny', color: '#FFD700' },
    { key: 'stattrak', label: 'ST™ StatTrak', color: '#FF8C00' },
    { key: 'vfx', label: '✦ VFX', color: '#a78bfa' },
  ];

  const tabProgress = {
    base: { collected: collectedItems, total: totalItems, pct: completionPercentage },
    shiny: { collected: shinyCollected, total: shinyTotal, pct: shinyTotal > 0 ? ((shinyCollected / shinyTotal) * 100).toFixed(1) : '0.0' },
    stattrak: { collected: statTrakCollected, total: statTrakTotal, pct: statTrakTotal > 0 ? ((statTrakCollected / statTrakTotal) * 100).toFixed(1) : '0.0' },
    vfx: { collected: vfxCombinedCollected, total: vfxCombinedTotal, pct: vfxCombinedTotal > 0 ? ((vfxCombinedCollected / vfxCombinedTotal) * 100).toFixed(1) : '0.0' },
  };

  const currentTab = COLLECTION_TABS.find(t => t.key === activeTab);
  const currentProgress = tabProgress[activeTab];

  return (
    <div className="collection-container">
      <h2>📊 Collection Progress</h2>

      {/* Tab buttons */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
        {COLLECTION_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 16px',
              background: activeTab === tab.key ? `${tab.color}22` : 'rgba(255,255,255,0.03)',
              border: `2px solid ${activeTab === tab.key ? tab.color : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '8px',
              color: activeTab === tab.key ? tab.color : '#888',
              fontWeight: activeTab === tab.key ? '700' : '500',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{
        marginTop: '16px',
        padding: '20px',
        background: `linear-gradient(135deg, ${currentTab.color}18, transparent)`,
        border: `2px solid ${currentTab.color}4D`,
        borderRadius: '12px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '36px', fontWeight: 'bold', color: currentTab.color }}>
          {currentProgress.collected} / {currentProgress.total}
        </div>
        <div style={{ fontSize: '14px', color: '#888', marginTop: '4px' }}>
          {currentProgress.pct}% Complete
        </div>
        <div style={{
          marginTop: '12px',
          height: '12px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${currentProgress.pct}%`,
            background: `linear-gradient(90deg, ${currentTab.color}, ${currentTab.color}AA)`,
            transition: 'width 0.5s'
          }} />
        </div>
      </div>

      {activeTab === 'base' && <>
      {/* Collection Milestones */}
      <div style={{ marginTop: '32px' }}>
        <h2 style={{ marginBottom: '16px' }}>🏆 Collection Milestones</h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '16px'
        }}>
          {COLLECTION_MILESTONES.map(milestone => {
            const isCompleted = checkMilestoneCompletion(milestone);
            const isClaimed = claimedRewards[milestone.id];
            const canClaim = isCompleted && !isClaimed;

            return (
              <div
                key={milestone.id}
                style={{
                  padding: '20px',
                  background: isClaimed
                    ? 'linear-gradient(135deg, rgba(92, 184, 92, 0.1), transparent)'
                    : canClaim
                    ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), transparent)'
                    : 'rgba(255, 255, 255, 0.02)',
                  border: `2px solid ${isClaimed ? '#5CB85C' : canClaim ? '#FFD700' : 'rgba(255, 255, 255, 0.1)'}`,
                  borderRadius: '12px',
                  position: 'relative',
                  opacity: isCompleted || isClaimed ? 1 : 0.6,
                  transition: 'all 0.3s'
                }}
              >
                {isClaimed && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    fontSize: '24px',
                    color: '#5CB85C'
                  }}>
                    ✓
                  </div>
                )}
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {milestone.icon}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px', color: '#fff' }}>
                  {milestone.name}
                </div>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>
                  {milestone.description}
                </div>
                <div style={{
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '11px', color: '#FFD700', fontWeight: 'bold', marginBottom: '6px' }}>
                    REWARDS:
                  </div>
                  <div style={{ fontSize: '12px', color: '#fff' }}>
                    {formatRewards(milestone.rewards)}
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '12px' }}>
                  {milestone.tiers.map((tier, idx) => {
                    const tierItems = allItems.filter(item => item.rarity === tier);
                    const tierCollected = tierItems.filter(item => ownedItems[item.id]).length;
                    const tierComplete = tierCollected === tierItems.length;
                    return (
                      <div key={tier} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                        color: tierComplete ? '#5CB85C' : '#888'
                      }}>
                        <span>{allRarities[tier].name}:</span>
                        <span>{tierCollected}/{tierItems.length} {tierComplete ? '✓' : ''}</span>
                      </div>
                    );
                  })}
                </div>
                {canClaim && (
                  <button
                    onClick={() => claimMilestoneReward(milestone)}
                    disabled={claiming}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      cursor: claiming ? 'not-allowed' : 'pointer',
                      opacity: claiming ? 0.5 : 1
                    }}
                  >
                    {claiming ? 'CLAIMING...' : '🎁 CLAIM REWARD'}
                  </button>
                )}
                {isClaimed && (
                  <div style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(92, 184, 92, 0.2)',
                    border: '2px solid #5CB85C',
                    borderRadius: '8px',
                    color: '#5CB85C',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    ✓ CLAIMED
                  </div>
                )}
                {!isCompleted && !isClaimed && (
                  <div style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#666',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    🔒 LOCKED
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {itemsByRarity.map(({ rarity, items }) => {
        const rarityData = allRarities[rarity];
        const collectedInRarity = items.filter(item => ownedItems[item.id]).length;

        return (
          <div key={rarity} className="collection-rarity-group" style={{ marginTop: '32px' }}>
            <h3 style={{ color: rarityData.color, marginBottom: '16px' }}>
              {rarityData.name} ({collectedInRarity}/{items.length})
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: '16px'
            }}>
              {items.map(item => {
                const owned = ownedItems[item.id];
                const isOwned = !!owned;

                return (
                  <div
                    key={item.id}
                    className="collection-item"
                    style={{
                      padding: '16px',
                      background: isOwned
                        ? `linear-gradient(135deg, ${rarityData.color}33, transparent)`
                        : 'rgba(255, 255, 255, 0.02)',
                      border: `2px solid ${isOwned ? rarityData.color : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: '8px',
                      textAlign: 'center',
                      position: 'relative',
                      opacity: isOwned ? 1 : 0.4,
                      transition: 'all 0.2s'
                    }}
                  >
                    {isOwned && (
                      <div style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        fontSize: '20px',
                        color: '#5CB85C'
                      }}>
                        ✓
                      </div>
                    )}
                    <div style={{
                      fontSize: '48px',
                      marginBottom: '8px',
                      filter: isOwned ? 'none' : 'grayscale(100%)'
                    }}>
                      <ItemIcon item={item} size={48} />
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: isOwned ? '#fff' : '#666',
                      fontWeight: isOwned ? 'bold' : 'normal',
                      marginBottom: '4px'
                    }}>
                      {item.name}
                    </div>
                    {isOwned && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                        {owned.count > 1 && (
                          <div style={{ marginBottom: '4px' }}>
                            x{owned.count} owned
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                          {owned.hasNormal && (
                            <span style={{ color: rarityData.color }}>●</span>
                          )}
                          {owned.hasShiny && (
                            <span style={{ color: '#FFD700' }}>✨</span>
                          )}
                        </div>
                      </div>
                    )}
                    {!isOwned && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                        Not collected
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      </>}

      {/* Shiny Collection Tab */}
      {activeTab === 'shiny' && (
        <>
          {itemsByRarity.map(({ rarity, items }) => {
            const rarityData = allRarities[rarity];
            const shinyInRarity = items.filter(item => ownedItems[item.id]?.hasShiny).length;
            return (
              <div key={rarity} style={{ marginTop: '32px' }}>
                <h3 style={{ color: rarityData.color, marginBottom: '16px' }}>
                  {rarityData.name} ({shinyInRarity}/{items.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '16px'
                }}>
                  {items.map(item => {
                    const owned = ownedItems[item.id];
                    const hasShiny = !!owned?.hasShiny;
                    return (
                      <div key={item.id} style={{
                        padding: '16px',
                        background: hasShiny
                          ? 'linear-gradient(135deg, #FFD70033, transparent)'
                          : 'rgba(255, 255, 255, 0.02)',
                        border: `2px solid ${hasShiny ? '#FFD700' : 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '8px',
                        textAlign: 'center',
                        position: 'relative',
                        opacity: hasShiny ? 1 : 0.4,
                        transition: 'all 0.2s'
                      }}>
                        {hasShiny && (
                          <div style={{ position: 'absolute', top: '4px', right: '4px', fontSize: '16px' }}>✨</div>
                        )}
                        <div style={{
                          fontSize: '48px',
                          marginBottom: '8px',
                          filter: hasShiny ? 'none' : 'grayscale(100%)'
                        }}>
                          <ItemIcon item={item} size={48} />
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: hasShiny ? '#FFD700' : '#666',
                          fontWeight: hasShiny ? 'bold' : 'normal',
                          marginBottom: '4px'
                        }}>
                          {item.name}
                        </div>
                        <div style={{ fontSize: '11px', color: hasShiny ? '#FFD700' : '#666' }}>
                          {hasShiny ? '✨ Shiny' : 'Not collected'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* StatTrak Collection Tab */}
      {activeTab === 'stattrak' && (
        <>
          {itemsByRarity.map(({ rarity, items }) => {
            const rarityData = allRarities[rarity];
            const stInRarity = items.filter(item => ownedItems[item.id]?.hasStatTrak).length;
            return (
              <div key={rarity} style={{ marginTop: '32px' }}>
                <h3 style={{ color: rarityData.color, marginBottom: '16px' }}>
                  {rarityData.name} ({stInRarity}/{items.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '16px'
                }}>
                  {items.map(item => {
                    const owned = ownedItems[item.id];
                    const hasST = !!owned?.hasStatTrak;
                    return (
                      <div key={item.id} style={{
                        padding: '16px',
                        background: hasST
                          ? 'linear-gradient(135deg, #FF8C0033, transparent)'
                          : 'rgba(255, 255, 255, 0.02)',
                        border: `2px solid ${hasST ? '#FF8C00' : 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '8px',
                        textAlign: 'center',
                        opacity: hasST ? 1 : 0.4,
                        transition: 'all 0.2s'
                      }}>
                        <div style={{
                          fontSize: '48px',
                          marginBottom: '8px',
                          filter: hasST ? 'none' : 'grayscale(100%)'
                        }}>
                          <ItemIcon item={item} size={48} />
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: hasST ? '#FF8C00' : '#666',
                          fontWeight: hasST ? 'bold' : 'normal',
                          marginBottom: '4px'
                        }}>
                          {item.name}
                        </div>
                        <div style={{ fontSize: '11px', color: hasST ? '#FF8C00' : '#666' }}>
                          {hasST ? 'ST™ StatTrak' : 'Not collected'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* VFX Collection Tab */}
      {activeTab === 'vfx' && (
        <>
          {/* VFX Effects Grid */}
          <div style={{ marginTop: '32px' }}>
            <h3 style={{ color: '#a78bfa', marginBottom: '16px' }}>
              VFX Effects ({vfxEffectsCollected}/{vfxEffectsTotal})
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '16px'
            }}>
              {VFX_EFFECTS.map(effect => {
                const isOwned = allOwnedVfx.has(effect.id);
                return (
                  <div key={effect.id} style={{
                    padding: '20px',
                    background: isOwned
                      ? 'linear-gradient(135deg, #a78bfa33, transparent)'
                      : 'rgba(255, 255, 255, 0.02)',
                    border: `2px solid ${isOwned ? '#a78bfa' : 'rgba(255, 255, 255, 0.1)'}`,
                    borderRadius: '8px',
                    textAlign: 'center',
                    opacity: isOwned ? 1 : 0.4,
                    transition: 'all 0.2s'
                  }}>
                    {isOwned && (
                      <div style={{ fontSize: '20px', color: '#5CB85C', marginBottom: '8px' }}>✓</div>
                    )}
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: isOwned ? '#a78bfa' : '#666',
                      marginBottom: '4px'
                    }}>
                      {effect.name}
                    </div>
                    <div style={{ fontSize: '11px', color: isOwned ? '#999' : '#555' }}>
                      {effect.description}
                    </div>
                    {!isOwned && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                        Not collected
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* VFX Items Grid */}
          {itemsByRarity.map(({ rarity, items }) => {
            const rarityData = allRarities[rarity];
            const vfxInRarity = items.filter(item => (ownedItems[item.id]?.vfxSet.size || 0) > 0).length;
            return (
              <div key={rarity} style={{ marginTop: '32px' }}>
                <h3 style={{ color: rarityData.color, marginBottom: '16px' }}>
                  {rarityData.name} ({vfxInRarity}/{items.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '16px'
                }}>
                  {items.map(item => {
                    const owned = ownedItems[item.id];
                    const hasVfx = (owned?.vfxSet.size || 0) > 0;
                    return (
                      <div key={item.id} style={{
                        padding: '16px',
                        background: hasVfx
                          ? 'linear-gradient(135deg, #a78bfa33, transparent)'
                          : 'rgba(255, 255, 255, 0.02)',
                        border: `2px solid ${hasVfx ? '#a78bfa' : 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '8px',
                        textAlign: 'center',
                        opacity: hasVfx ? 1 : 0.4,
                        transition: 'all 0.2s'
                      }}>
                        <div style={{
                          fontSize: '48px',
                          marginBottom: '8px',
                          filter: hasVfx ? 'none' : 'grayscale(100%)'
                        }}>
                          <ItemIcon item={item} size={48} />
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: hasVfx ? '#a78bfa' : '#666',
                          fontWeight: hasVfx ? 'bold' : 'normal',
                          marginBottom: '4px'
                        }}>
                          {item.name}
                        </div>
                        <div style={{ fontSize: '11px', color: hasVfx ? '#a78bfa' : '#666' }}>
                          {hasVfx ? `✦ VFX` : 'Not collected'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default CollectionTracker;
